import { useState, useCallback } from 'react';
import { gql, useSubscription } from '@apollo/client';
import { messageHeaders } from 'features/common/fragments';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';

const COMMAND_EXECUTION_ERROR = gql`
  subscription commandExecutionError {
    commandExecutionError {
      code
      data
      type
      headers {
        ...messageHeaders
      }
    }
  }
  ${messageHeaders}
`;

const PERSISTENCE_QUERY = gql`
  {
    serverErrorHandlingEventKeys @client {
      keys {
        correlationId
      }
    }
  }
`;

const persistenceType = {
  state: 'state',
  apollo: 'apollo'
};

const useApolloPersistence = () => {
  const client = useApolloClient();

  const getKeys = useCallback(() => {
    const {
      serverErrorHandlingEventKeys: { keys }
    } = client.readQuery({ query: PERSISTENCE_QUERY });

    return keys;
  }, [client]);

  const get = useCallback(
    correlationId => {
      const keys = getKeys();
      const key = keys.find(z => z.correlationId === correlationId);
      return key;
    },
    [getKeys]
  );

  const set = useCallback(
    correlationId => {
      const keys = getKeys();
      client.writeQuery({
        query: PERSISTENCE_QUERY,
        data: {
          serverErrorHandlingEventKeys: {
            __typename: 'serverErrorHandlingEventKeys',
            keys: [...keys, { correlationId }]
          }
        }
      });
    },
    [client, getKeys]
  );

  const persistKeys = useCallback(
    keys => {
      client.writeQuery({
        query: PERSISTENCE_QUERY,
        data: {
          serverErrorHandlingEventKeys: {
            __typename: 'serverErrorHandlingEventKeys',
            keys
          }
        }
      });
    },
    [client]
  );

  const evict = useCallback(
    correlationId => {
      const keys = [...getKeys()];
      const others = keys.filter(z => z.correlationId !== correlationId);
      persistKeys(others);
    },
    [getKeys, persistKeys]
  );

  return { get, set, evict };
};

const useStatePersistence = () => {
  const [eventKeys, setEventKeys] = useState([]);

  const get = useCallback(
    correlationId => {
      const key = eventKeys.find(z => z.correlationId === correlationId);
      return key;
    },
    [eventKeys]
  );

  const set = useCallback(correlationId => {
    setEventKeys(prev => [...prev, { correlationId }]);
  }, []);

  const evict = useCallback(correlationId => {
    setEventKeys(prev => [...prev.filter(z => z.correlationId !== correlationId)]);
  }, []);

  return { get, set, evict };
};

export const useServerErrorHandling = ({ effect, global, persistence = persistenceType.state }) => {
  const statePersistence = useStatePersistence();
  const apolloPersistence = useApolloPersistence();
  const targetPersistence = persistence === persistenceType.state ? statePersistence : apolloPersistence;
  const { get, set, evict } = targetPersistence;
  const { t, i18n } = useTranslation();

  useSubscription(COMMAND_EXECUTION_ERROR, {
    onData: event => {
      const data = event.data?.data?.commandExecutionError;
      let message = data?.code;
      if (message && i18n.exists(message)) {
        message = t(message, data?.data);
      }

      const { nbbCorrelationId } = data?.headers ?? {};
      if (global) {
        const isValidationError = data?.type?.includes('ValidationException');
        effect({ message, correlationId: isValidationError ? null : nbbCorrelationId });
      } else {
        if (!nbbCorrelationId) return;
        const keys = get(nbbCorrelationId);
        if (keys) {
          const input = { ...keys, message };
          effect(input);
        }
      }
    }
  });

  const storeKey = useCallback(
    key => {
      set(key.correlationId);
    },
    [set]
  );

  const evictKey = useCallback(
    key => {
      evict(key.correlationId);
    },
    [evict]
  );

  return { storeKey, evictKey };
};
